// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/LicenseDataSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, theme } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const LicenseDataSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const lic = data?.licenseData;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = lic && (lic.licenseType || lic.platform?.length || lic.version || lic.vendor);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <Collapse
      className="collapsed-content-fit"
      collapsible="icon"
      defaultActiveKey={['license-info']}
      style={{ marginBottom: 16 }}
      items={[
        {
          key: 'license-info',
          label: (
            <Flex align="center" gap={8}>
              <KeyOutlined style={{ color: token.colorPrimary }} />
              <span style={{ fontWeight: 600 }}>{translateX('license_info')}</span>
            </Flex>
          ),
          showArrow: false,
          children: (
            <Descriptions
              bordered
              size="small"
              column={1}
              layout={isMobile ? 'vertical' : 'horizontal'}
              styles={{ label: { fontWeight: 500 } }}
            >
              {lic.licenseType && (
                <Descriptions.Item label={translateX('license_type')}>
                  <Tag color="blue">{lic.licenseType}</Tag>
                </Descriptions.Item>
              )}
              {lic.platform?.length > 0 && (
                <Descriptions.Item label={translateX('platform')}>
                  <Flex wrap="wrap" gap={4}>
                    {lic.platform.map((p, i) => (
                      <Tag key={i} style={{ margin: 0 }}>{p}</Tag>
                    ))}
                  </Flex>
                </Descriptions.Item>
              )}
              {lic.version && (
                <Descriptions.Item label={translateX('version')}>
                  {lic.version}
                </Descriptions.Item>
              )}
              {lic.licenseScope && (
                <Descriptions.Item label={translateX('license_scope')}>
                  <Tag>{lic.licenseScope}</Tag>
                </Descriptions.Item>
              )}
              {lic.maxUsers !== undefined && lic.maxUsers !== null && (
                <Descriptions.Item label={translateX('max_users')}>
                  {lic.maxUsers}
                </Descriptions.Item>
              )}
              {lic.maxDevices !== undefined && lic.maxDevices !== null && (
                <Descriptions.Item label={translateX('max_devices')}>
                  {lic.maxDevices}
                </Descriptions.Item>
              )}
              {lic.activationMethod && (
                <Descriptions.Item label={translateX('activation_method')}>
                  {lic.activationMethod}
                </Descriptions.Item>
              )}
              {lic.includesUpdates !== undefined && (
                <Descriptions.Item label={translateX('includes_updates')}>
                  {lic.includesUpdates ? (
                    <Tag color="green">{translateX('yes')}</Tag>
                  ) : (
                    <Tag color="red">{translateX('no')}</Tag>
                  )}
                </Descriptions.Item>
              )}
              {lic.includesSupport !== undefined && (
                <Descriptions.Item label={translateX('includes_support')}>
                  {lic.includesSupport ? (
                    <Tag color="green">{translateX('yes')}</Tag>
                  ) : (
                    <Tag color="red">{translateX('no')}</Tag>
                  )}
                </Descriptions.Item>
              )}
              {lic.supportLevel && (
                <Descriptions.Item label={translateX('support_level')}>
                  <Tag color={lic.supportLevel === '24x7' ? 'green' : lic.supportLevel === 'phone' ? 'blue' : 'default'}>
                    {lic.supportLevel}
                  </Tag>
                </Descriptions.Item>
              )}
              {lic.deliveryMethod && (
                <Descriptions.Item label={translateX('delivery_method')}>
                  {lic.deliveryMethod}
                </Descriptions.Item>
              )}
              {lic.compatibility?.length > 0 && (
                <Descriptions.Item label={translateX('compatibility')}>
                  <Flex wrap="wrap" gap={4}>
                    {lic.compatibility.map((c, i) => (
                      <Tag key={i} style={{ margin: 0 }}>{c}</Tag>
                    ))}
                  </Flex>
                </Descriptions.Item>
              )}
              {lic.vendor && (
                <Descriptions.Item label={translateX('vendor')}>
                  {lic.vendor}
                </Descriptions.Item>
              )}
              {lic.productUrl && (
                <Descriptions.Item label={translateX('product_url')}>
                  <a href={lic.productUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                    {lic.productUrl}
                  </a>
                </Descriptions.Item>
              )}
            </Descriptions>
          ),
        },
      ]}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default LicenseDataSection;
